// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — upDawgs.js   ·   ✹ UP DAWGS, THE MINERIUM'S MONEY
// ────────────────────────────────────────────────────────────────────
// Pierre's own earnable currency, and the only thing the Minerium
// takes. Every chair, every seed, every sky, every door.
//
// ── ⭐ THE ACQUIRE-ONLY LAW (caP'n, this session) ───────────────────
//
//     "you can never exchange UP Dawgs for anything, you can only
//      exchange to obtain them."
//
// Dawgs flow IN at the Well and never flow OUT. You may turn Chocolate
// Coins, Snake Eyes Coins, Sol, Crumbs or a Divine Crest into Dawgs;
// you may never turn Dawgs into any of them.
//
// ⚠ THIS IS NOT FLAVOUR, IT IS THE LOAD-BEARING GUARD ON THE WHOLE
// EMPIRE'S RESERVE. The Minerium pays Dawgs on a TIMER. A timer that
// can reach DC is an AFK machine that prints the Empire's reserve
// currency while caP'n is asleep, and THE FAUCET LAW (PIERRE_PRIME L8)
// exists precisely to forbid that shape. One-way is what makes a
// passive faucet legal: the money it makes can only ever be spent on
// furniture.
//
// It is enforced in THREE places, on purpose, because a single gate on
// money is a single point of failure:
//   1 · `planExchange()` in progression.js refuses UD as a source.
//   2 · `executeExchange()` re-checks before it debits anything.
//   3 · `CrestExchange.jsx` never offers UD in the source picker.
// Deleting any one of them must not open the door. That is the test.
//
// ── THE TWO-CURRENCY LAW ────────────────────────────────────────────
// `ieEconomy.assertTwoCurrencies` says a title has exactly two earnable
// numbers: its own, and ◆. Before this file Pierre had ZERO of its own
// — it was a bank with no money. Dawgs is Pierre's one. There must
// never be a second, and an XP pool or a "Renown" score would be one.
//
// ── EVERY MOVEMENT IS A DELTA ON THE LEAF ───────────────────────────
// PIERRE_PRIME L9. Nothing in this file reads a balance and writes it
// back. `deltaCurrency()` is the canonical writer and it aborts rather
// than going negative, so "you cannot afford it" is decided by the
// database and not by a number a client read a moment ago.
//
// ── FIREBASE ────────────────────────────────────────────────────────
//   profiles/{uid}/upDawgs                 the balance (delta only)
//   games/pierre/exchange/rates/UD         the rate row (console-tunable)
//   games/pierre/dawgs/{uid}/{pushId}      the ledger (append-only)
// No new top-level node. `games/pierre/*` and `profiles/{uid}/*` are
// both already in the rules tree.
// ════════════════════════════════════════════════════════════════════

import { ref, push, get, query, limitToLast, orderByKey } from "firebase/database";
import { db } from "./firebase";
import {
  deltaCurrency, ensureCurrency, listenToCurrency, PIERRE_BASE,
} from "./ieEconomy";

export const UD_ID = "UD";

// ── THE ROW ─────────────────────────────────────────────────────────
// ⚠ THIS IS A SEED, NOT A SETTING. `ensureDawgsRegistered()` writes it
// once if the node is empty and is inert forever after, so a rate caP'n
// tunes in the Firebase console is never un-tuned by somebody opening
// the app. That is the entire difference between `ensureCurrency` and
// `registerCurrency`, and it is why boot code calls the first one.
//
// ── ⚠ perCrest HERE IS A PURCHASE RATE, NOT AN EARN RATE ───────────
// THE TOIL LAW (L6) sets a rate so that EARNING one DC costs the same
// effort in every title. It cannot apply to Dawgs, because Dawgs can
// never be earned into DC — the acquire-only law means this row is only
// ever read in the DC → UD direction. So do NOT "correct" this number
// toward the 3–5-DC-an-hour reference; that reference is about a door
// this currency does not have.
//
// What it actually has to be worth: a Divine Crest is endgame money and
// Dawgs are bread, so spending one has to feel like a real shortcut
// rather than an hour skipped. A mid-game holding (five crop beds, a
// Chao Pool, an Anima Tree) makes roughly 710 Dawgs an hour, and a full
// eight-hour barn is a few thousand. 6,000 Dawgs to the Crest is
// therefore about a day of the holding's output — worth converting for,
// and never so cheap that a Crest reads as small change.
//
// ⚠ 710/hour is DESIGNED, not MEASURED — nobody has played an hour of
// this yet — so the row ships `provisional: true` and the Well renders
// its ⚠ marker on the leg (L13). One field in the console fixes it the
// day a real hour is timed. No deploy, either side.
export const UD_ROW = {
  currencyId: UD_ID,
  name: "UP Dawgs",
  short: "UD",
  icon: "✹",
  owningApp: "pierre",
  path: "upDawgs",
  perCrest: 6000,
  active: true,
  provisional: true,
  measuredAt: null,
  acquireOnly: true,
  tint: "#9ad46a",
  note: "Minerium scrip. Acquire-only — the Well sells Dawgs and never buys them.",
};

// ── THE ACQUIRE-ONLY REGISTER ───────────────────────────────────────
// A SET, not an `=== "UD"`. The day a second acquire-only currency
// exists, it is one entry here and every gate already honours it — and
// nobody has to go find three hardcoded comparisons. (L16, the registry
// seam, applied to a law instead of a currency.)
export const ACQUIRE_ONLY = new Set([UD_ID]);

export const isAcquireOnly = (currencyId) => ACQUIRE_ONLY.has(currencyId);

// The one sentence every refusal says, so the Well, the shop and the
// console cannot disagree about WHY.
export const ACQUIRE_ONLY_REASON =
  "Dawgs only come in. The Well will sell you Dawgs all day and it will " +
  "never buy them back — that is the deal that lets your Minerium pay you " +
  "while you are away.";

// ── PATHS ───────────────────────────────────────────────────────────
export const DAWGS_LEDGER_PATH = `${PIERRE_BASE}/dawgs`;

// ⚠ MIRROR OF THE RULES CLAMP, exactly as grantCrests does it. The
// Layer One rules refuse any single currency write that jumps more
// than CLAMP_MAX_DELTA, so code must stay BELOW the rule or a
// legitimate payout fails in production and looks like a Firebase bug.
// A large grant is CHUNKED, never truncated — truncating is quiet
// theft, and a player who leaves a full barn for a week is entitled to
// every Dawg of it.
export const DAWGS_GRANT_CEILING = 2500;

// The rate row is all `deltaCurrency` needs to find the leaf. We own
// this currency, so we know its path without a read — passing the row
// here is not a shortcut past the registry, it IS the registry entry.
const asRates = () => ({ [UD_ID]: UD_ROW });

// ── REGISTER ────────────────────────────────────────────────────────
// Safe on a boot path. Seeds once, inert forever after.
export async function ensureDawgsRegistered() {
  try { return await ensureCurrency(UD_ROW); }
  catch { return "failed"; }   // never let a boot path throw on money
}

// ── READ ────────────────────────────────────────────────────────────
export const listenToDawgs = (uid, cb) =>
  listenToCurrency(uid, UD_ID, cb, asRates());

export async function getDawgs(uid) {
  if (!uid) return 0;
  try {
    const snap = await get(ref(db, `profiles/${uid}/upDawgs`));
    return snap.exists() ? (snap.val() || 0) : 0;
  } catch { return 0; }
}

// ── GRANT ───────────────────────────────────────────────────────────
// GRANT BEFORE FLAG, same as every faucet in the Empire: pay first,
// mark the source claimed second. A crash between them costs a
// duplicate payout, which is recoverable; the reverse costs a player
// their harvest, which is not.
//
// `sourceKey` names the FAUCET, never the app — "minerium:harvest:crop_bed",
// "pierre:quest:terror_first_duel", "pierre:well:CC". When something
// inflates you want to know which door it came through.
export async function grantDawgs(uid, amount, sourceKey = "unknown") {
  if (!uid) return 0;
  const amt = Math.floor(Number(amount) || 0);
  if (amt <= 0) return 0;

  let paid = 0;
  let left = amt;
  while (left > 0) {
    const bite = Math.min(left, DAWGS_GRANT_CEILING);
    const res = await deltaCurrency(uid, UD_ID, bite, { owningApp: "pierre", rates: asRates() });
    // A refused bite stops the ladder. Whatever already landed stays
    // landed and is reported honestly, rather than being rolled back —
    // the player is not poorer for a partial payout, only shorter.
    if (res === null) break;
    paid += bite;
    left -= bite;
  }
  if (paid <= 0) return 0;

  // ── THE LEDGER. One row for the real amount, even when the payout
  // went out in bites, so the audit trail reads as the single event it
  // actually was. Append-only; nothing ever edits a row.
  try {
    await push(ref(db, `${DAWGS_LEDGER_PATH}/${uid}`), {
      amount: paid,
      requested: amt,
      sourceKey: String(sourceKey || "unknown"),
      at: Date.now(),
    });
  } catch { /* the money landed; a missing ledger row is not worth failing a harvest over */ }

  return paid;
}

// ── SPEND ───────────────────────────────────────────────────────────
// Returns the amount actually taken, or 0. ⚠ It ABORTS rather than
// clamping — a spend that "succeeds" for less than it asked is how a
// shop hands out an item nobody paid for.
export async function spendDawgs(uid, amount) {
  if (!uid) return 0;
  const amt = Math.floor(Number(amount) || 0);
  if (amt <= 0) return 0;
  // ⚠ CHUNKED, LIKE THE GRANT (2026-09-15). One write of −6,500 is over the
  // rules clamp, so the day the testing till comes off, every Anima Tree,
  // every Mythical and three of the five bundles would be refused with
  // "not enough Dawgs" against a full wallet. A spend that fails part way
  // is put BACK — a spend is all or nothing, or it is a shop handing out a
  // discount nobody priced.
  let taken = 0, left = amt;
  while (left > 0) {
    const bite = Math.min(left, DAWGS_GRANT_CEILING);
    const res = await deltaCurrency(uid, UD_ID, -bite, { owningApp: "pierre", rates: asRates() });
    if (res === null) break;
    taken += bite;
    left -= bite;
  }
  if (taken === amt) return amt;
  if (taken > 0) await grantDawgs(uid, taken, "refund:spend_partial").catch(() => {});
  return 0;
}

// ── REFUND ──────────────────────────────────────────────────────────
// Named separately from grantDawgs on purpose. A refund is not a
// faucet: it puts back money that was already the player's, and it must
// be findable in the ledger as its own kind of event when something has
// gone wrong.
export async function refundDawgs(uid, amount, sourceKey = "refund") {
  return grantDawgs(uid, amount, `refund:${sourceKey}`);
}

// ── THE LEDGER ──────────────────────────────────────────────────────
export async function getDawgsLedger(uid, count = 40) {
  if (!uid) return [];
  try {
    const snap = await get(query(
      ref(db, `${DAWGS_LEDGER_PATH}/${uid}`), orderByKey(), limitToLast(count)
    ));
    const out = [];
    snap.forEach((c) => { out.push({ id: c.key, ...(c.val() || {}) }); });
    return out.reverse();
  } catch { return []; }
}

const upDawgsApi = {
  UD_ID, UD_ROW, ACQUIRE_ONLY, isAcquireOnly, ACQUIRE_ONLY_REASON,
  ensureDawgsRegistered, listenToDawgs, getDawgs,
  grantDawgs, spendDawgs, refundDawgs, getDawgsLedger,
};

export default upDawgsApi;
